<template>
  <div class="max-w-4xl mx-auto">
    <div class="bg-white rounded-lg shadow-md p-8">
      <div class="space-y-6">
        <!-- Folder Upload Section -->
        <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
          <div class="space-y-4">
            <div class="flex justify-center">
              <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"></path>
              </svg>
            </div>
            
            <div class="space-y-2">
              <h3 class="text-lg font-semibold text-gray-900">
                Upload Folder of Skyn Biosensor Data Files
              </h3>
              <p class="text-sm text-gray-500">
                Select a folder containing CSV (.csv) or Excel (.xlsx) files
              </p>
              <p class="text-sm text-gray-500">
                Each file should follow format: <strong>&lt;subid&gt;_&lt;dataset_id&gt;</strong> (e.g., 123_001.csv)
              </p>
            </div>
            
            <div class="flex justify-center space-x-4">
              <input
                id="folder-upload"
                type="file"
                webkitdirectory
                directory
                multiple
                accept=".csv,.xlsx"
                @change="handleFolderUpload"
                class="hidden"
              />
              <label for="folder-upload" class="cursor-pointer">
                <div class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  Choose Folder
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Cohort Files Display -->
        <div v-if="cohortFiles.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 class="font-semibold text-blue-800 mb-3">Valid Files ({{ cohortFiles.length }})</h4>
          <div class="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div class="space-y-2">
              <div
                v-for="(file, index) in sortedCohortFiles"
                :key="index"
                class="flex items-center justify-between p-2 bg-white rounded border"
              >
                <div class="flex items-center space-x-3">
                  <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm text-gray-700">{{ file.filename }}</span>
                </div>
                <div class="text-xs text-gray-500">
                  SubID: {{ file.subid }}, Dataset: {{ file.datasetId }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Excluded Files Display -->
        <div v-if="invalidFilenames.length > 0" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h4 class="font-semibold text-gray-800">Excluded Files</h4>
              <p class="text-sm text-gray-600">{{ invalidFilenames.length }} files were excluded from processing</p>
            </div>
            <button @click="openFilenameCorrectionModal" class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
              <span>Reconfigure</span>
            </button>
          </div>
          <div class="max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div class="space-y-2">
              <div
                v-for="(file, index) in invalidFilenames"
                :key="index"
                class="flex items-center space-x-3 p-2 bg-white rounded border"
              >
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span class="text-sm text-gray-700">{{ file.filename }}</span>
                <span class="text-xs text-gray-500">{{ file.reason }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload Button for Cohort -->
        <div v-if="cohortFiles.length > 0" class="flex justify-center">
          <button
            @click="uploadCohortFiles"
            :disabled="cohortUploading"
            class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <svg v-if="cohortUploading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span>{{ cohortUploading ? 'Uploading Cohort...' : `Upload ${cohortFiles.length} Files` }}</span>
          </button>
        </div>

        <!-- Upload Progress -->
        <div v-if="cohortUploading" class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">
              Processing cohort... 
              <span v-if="currentProcessingFile" class="text-blue-600">({{ currentProcessingFile }})</span>
            </span>
            <span class="text-sm text-gray-500">{{ cohortUploadProgress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-green-600 h-2 rounded-full transition-all duration-300" :style="{ width: cohortUploadProgress + '%' }"></div>
          </div>
          <div v-if="currentProcessingFile" class="mt-2 text-xs text-gray-600">
            Currently processing: {{ currentProcessingFile }}
          </div>
        </div>

        <!-- Upload Result -->
        <div v-if="uploadResult" class="space-y-4">
          <!-- Summary -->
          <div class="border rounded-lg p-4" :class="uploadResult.failedFiles === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'">
            <div class="flex items-center space-x-3 mb-3">
              <svg v-if="uploadResult.failedFiles === 0" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <svg v-else class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <h4 class="font-medium" :class="uploadResult.failedFiles === 0 ? 'text-green-900' : 'text-yellow-900'">
                  Cohort Processing Complete
                </h4>
                <p class="text-sm" :class="uploadResult.failedFiles === 0 ? 'text-green-700' : 'text-yellow-700'">
                  {{ uploadResult.successfulFiles }} of {{ uploadResult.totalFiles }} files processed successfully
                  <span v-if="uploadResult.failedFiles > 0">, {{ uploadResult.failedFiles }} failed</span>
                </p>
              </div>
            </div>
            
            <!-- Action buttons -->
            <div class="flex space-x-3">
              <button
                v-if="uploadResult.successfulFiles > 0"
                @click="goToSubjectDashboard"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Dashboard
              </button>
              <button
                v-if="uploadResult.failedFiles > 0"
                @click="retryFailedFiles"
                class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Retry Failed Files
              </button>
            </div>
          </div>

          <!-- Detailed Results -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h5 class="font-semibold text-gray-800 mb-3">File Processing Results</h5>
            <div class="space-y-2 max-h-64 overflow-y-auto">
              <div
                v-for="result in fileResults"
                :key="result.filename"
                class="flex items-center justify-between p-3 border rounded-lg"
                :class="result.status === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'"
              >
                <div class="flex items-center space-x-3">
                  <svg v-if="result.status === 'success'" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <svg v-else class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div>
                    <div class="font-medium" :class="result.status === 'success' ? 'text-green-800' : 'text-red-800'">
                      {{ result.filename }}
                    </div>
                    <div class="text-xs text-gray-600">
                      SubID: {{ result.subid }}, Dataset: {{ result.datasetId }}
                      <span v-if="result.status === 'success'">
                        • {{ result.processedRows }} rows • {{ result.dayCount }} days
                      </span>
                    </div>
                    <div v-if="result.status === 'failed'" class="text-xs text-red-600 mt-1">
                      {{ result.error }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Display -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 class="font-medium text-red-900">Upload Failed</h4>
              <p class="text-sm text-red-700">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cohort Modal for Invalid Filenames -->
    <CohortFilenameCorrectionModal
      :is-visible="showCohortModal"
      :files="cohortModalData"
      :valid-files="cohortFiles"
      @close="handleCohortModalClose"
      @confirm="handleCohortModalConfirm"
    />
  </div>
</template>

<script>
import axios from 'axios'
import CohortFilenameCorrectionModal from '../../components/CohortFilenameCorrectionModal.vue'
import { extractSubidAndDatasetId } from '../../utils/dataUploadConfiguration.js'
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import { processFileForUpload } from '../../utils/excelConverter.js'
import { useLabPath } from '../../composables/useLabPath.js'

export default {
  name: 'CohortUpload',
  inject: ['globalState', 'updateGlobalState', 'clearGlobalState', 'setBackButton', 'hideBackButton'],
  components: {
    CohortFilenameCorrectionModal
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
    return {
      cohortFiles: [],
      invalidFilenames: [],
      cohortUploading: false,
      cohortUploadProgress: 0,
      currentProcessingFile: null,
      showCohortModal: false,
      cohortModalData: [],
      uploadResult: null,
      error: null,
      fileResults: [],
      successfulFiles: [],
      failedFiles: []
    }
  },
  computed: {
    sortedCohortFiles() {
      return [...this.cohortFiles].sort((a, b) => {
        // Sort by SubID first (numerically)
        const subidA = parseInt(a.subid)
        const subidB = parseInt(b.subid)
        
        if (subidA !== subidB) {
          return subidA - subidB
        }
        
        // If SubIDs are the same, sort by Dataset ID (numerically)
        const datasetA = parseInt(a.datasetId)
        const datasetB = parseInt(b.datasetId)
        return datasetA - datasetB
      })
    }
  },
  methods: {
    goToSubjectDashboard() {
      this.$router.push(this.toLab('/subject-dashboard'))
    },
    async handleFolderUpload(event) {
      const files = Array.from(event.target.files)
      if (files.length === 0) return

      event.target.value = '' // Clear input value
      this.cohortFiles = [] // Reset state
      this.invalidFilenames = []
      this.showCohortModal = false
      this.cohortModalData = []
      this.error = null
      this.uploadResult = null

      const validFiles = files.filter(file => {
        const extension = file.name.toLowerCase().split('.').pop()
        return extension === 'csv' || extension === 'xlsx'
      })

      console.log('All files in folder:', files.map(f => f.name))
      console.log('Files with valid extensions:', validFiles.map(f => f.name))

      const validFilesWithIds = []
      const invalidFiles = []

      for (const file of validFiles) {
        console.log(`Processing file: ${file.name}`)
        const extracted = extractSubidAndDatasetId(file.name)
        console.log(`Extraction result for ${file.name}:`, extracted)
        
        if (extracted && extracted.subid && extracted.datasetId) {
          // Add ALL files with valid filename format to validFilesWithIds
          // Let the modal handle duplicate detection
          console.log(`Adding ${file.name} to valid files with IDs`)
          validFilesWithIds.push({ 
            filename: file.name,
            file_path: file.webkitRelativePath || file.name, // Store file path
            file: file, // Keep file object for now (will be removed later)
            subid: extracted.subid, 
            datasetId: extracted.datasetId
          })
        } else {
          console.log(`Adding ${file.name} to invalid files - extraction failed`)
          invalidFiles.push({ 
            filename: file.name,
            file: file, 
            reason: 'Invalid filename format', 
            subid: '', 
            datasetId: '' 
          })
        }
      }

      this.cohortFiles = validFilesWithIds
      this.invalidFilenames = invalidFiles

      console.log('Valid files with IDs:', validFilesWithIds.map(f => f.filename))
      console.log('Invalid files:', invalidFiles.map(f => f.filename))

      // Check for duplicates across ALL valid files (both initially valid and corrected)
      const allValidFiles = [...validFilesWithIds]
      const duplicateCheck = this.checkForDuplicates(allValidFiles)
      
      console.log('Duplicate check result:', duplicateCheck)
      console.log('Has duplicates:', duplicateCheck.hasDuplicates)
      console.log('Duplicate files:', duplicateCheck.duplicateFiles)
      
      if (duplicateCheck.hasDuplicates) {
        // Show modal with both invalid filenames AND duplicate files
        console.log('Showing modal for duplicates and invalid files')
        const modalData = [...invalidFiles, ...duplicateCheck.duplicateFiles]
        this.showCohortModal = true
        this.cohortModalData = modalData.map(item => ({ ...item, file: item.file }))
        console.log('Modal state set - showCohortModal:', this.showCohortModal)
        console.log('Modal data count:', this.cohortModalData.length)
      } else if (invalidFiles.length > 0) {
        // Only invalid filenames, no duplicates
        console.log('Showing modal for invalid filenames only')
        this.showCohortModal = true
        this.cohortModalData = invalidFiles.map(item => ({ ...item, file: item.file }))
      } else if (validFilesWithIds.length > 0) {
        console.log('No duplicates or invalid files, showing review modal')
        // Even with no issues, show the review modal to let user select which files to include
        this.showCohortModal = true
        this.cohortModalData = [] // Empty modal data since no files need correction
      } else {
        this.error = 'No valid files found in the selected folder'
      }
    },

    openFilenameCorrectionModal() {
      // Combine invalid filenames and any duplicate files for the modal
      const modalData = [
        ...this.invalidFilenames,
        // Check if there are any duplicate files that should be shown
        ...this.checkForDuplicates(this.cohortFiles).duplicateFiles
      ].map(item => ({
        filename: item.filename,
        subid: item.subid || '',
        datasetId: item.datasetId || '',
        reason: item.reason,
        file: item.file
      }))
      
      this.cohortModalData = modalData
      this.showCohortModal = true
    },

    handleCohortModalClose() {
      this.showCohortModal = false
    },

    handleCohortModalConfirm(data) {
      this.showCohortModal = false
      
      console.log('📋 Modal confirmed with data:', data)
      console.log('📋 Data length:', data.length)
      console.log('📋 Data filenames:', data.map(f => f.filename))
      
      // The modal's handleFinalConfirm() already filters out excluded files
      // So 'data' only contains files that were actually selected/included
      // We can directly use this data as our final cohort files
      
      // Clear existing state
      this.cohortFiles = []
      this.invalidFilenames = []
      
      // Add the confirmed files directly to cohortFiles
      data.forEach(item => {
        if (item.subid && item.datasetId) {
          this.cohortFiles.push({
            filename: item.filename,
            file: item.file,
            subid: item.subid,
            datasetId: item.datasetId
          })
        } else {
          // This shouldn't happen if modal validation worked correctly
          this.invalidFilenames.push({
            filename: item.filename,
            file: item.file,
            reason: item.reason || 'Filename not corrected or excluded',
            subid: item.subid || '',
            datasetId: item.datasetId || ''
          })
        }
      })
      
      console.log('✅ Final cohort files:', this.cohortFiles.map(f => f.filename))
      console.log('❌ Excluded files:', this.invalidFilenames.map(f => f.filename))

      // Show excluded files instead of error message
      if (this.invalidFilenames.length > 0) {
        // Don't show error, just display the excluded files
        this.error = null
      } else if (this.cohortFiles.length > 0) {
        this.uploadCohortFiles()
      } else {
        this.error = 'No valid files to upload after correction.'
      }
    },

    checkForDuplicates(files) {
      const subidDatasetMap = new Map()
      const duplicateFiles = []
      
      files.forEach(file => {
        const key = `${file.subid}_${file.datasetId}`
        if (subidDatasetMap.has(key)) {
          // Found a duplicate - add both the original and this file to duplicates
          const originalFile = subidDatasetMap.get(key)
          
          // Add original file if not already in duplicates
          if (!duplicateFiles.find(f => f.filename === originalFile.filename)) {
            duplicateFiles.push({
              filename: originalFile.filename,
              file: originalFile.file,
              reason: `Duplicate SubID-Dataset combination: ${originalFile.subid}_${originalFile.datasetId} also exists in: ${file.filename}`,
              subid: originalFile.subid,
              datasetId: originalFile.datasetId
            })
          }
          
          // Add current file to duplicates
          duplicateFiles.push({
            filename: file.filename,
            file: file.file,
            reason: `Duplicate SubID-Dataset combination: ${file.subid}_${file.datasetId} also exists in: ${originalFile.filename}`,
            subid: file.subid,
            datasetId: file.datasetId
          })
        } else {
          subidDatasetMap.set(key, file)
        }
      })
      
      return {
        hasDuplicates: duplicateFiles.length > 0,
        duplicateFiles: duplicateFiles
      }
    },

    async uploadCohortFiles() {
      if (this.cohortFiles.length === 0) return

      this.cohortUploading = true
      this.cohortUploadProgress = 0
      this.error = null
      this.uploadResult = null

      // Initialize results tracking
      this.fileResults = []
      this.successfulFiles = []
      this.failedFiles = []

      try {
        // Process each file individually
        for (let i = 0; i < this.cohortFiles.length; i++) {
          const fileData = this.cohortFiles[i]
          this.currentProcessingFile = fileData.filename
          this.cohortUploadProgress = Math.round(((i + 1) / this.cohortFiles.length) * 100)

          try {
            // Extract metadata for proper filename generation
            const metadata = {
              subid: fileData.subid,
              datasetId: fileData.datasetId
            }
            
            // Convert Excel files to CSV before upload
            const processedFile = await processFileForUpload(fileData.file, metadata)
            
            const formData = new FormData()
            formData.append('file', processedFile)
            formData.append('subid', fileData.subid)
            formData.append('dataset_id', fileData.datasetId)

            const response = await axios.post(buildApiUrl(API_ENDPOINTS.CONFIGURE_BIOSENSOR), formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              withCredentials: true
            })

            console.log(`📊 Upload response for ${fileData.filename}:`, response.data)
            console.log(`🔍 Response data type for ${fileData.filename}:`, typeof response.data)
            
            // Parse the response data if it's a string (JSON)
            let parsedResponseData = response.data
            if (typeof response.data === 'string') {
              try {
                // Handle NaN values in JSON by replacing them with null before parsing
                const sanitizedJson = response.data.replace(/:\s*NaN\s*/g, ': null')
                parsedResponseData = JSON.parse(sanitizedJson)
                console.log(`🔧 Parsed JSON response data for ${fileData.filename} (sanitized NaN values)`)
              } catch (error) {
                console.error(`❌ Failed to parse response data as JSON for ${fileData.filename}:`, error)
                const fileResult = {
                  filename: fileData.filename,
                  subid: fileData.subid,
                  datasetId: fileData.datasetId,
                  status: 'failed',
                  error: `Upload failed - invalid response format: ${error.message}`
                }
                this.fileResults.push(fileResult)
                this.failedFiles.push(fileResult)
                continue // Skip to next file
              }
            }

            // Use object destructuring to handle nested response structure
            const { data: responseData } = parsedResponseData || {}
            const { data: processedData } = responseData || {}
            
            console.log(`✅ Success flag for ${fileData.filename}:`, parsedResponseData.success)
            console.log(`🔍 Extracted processedData for ${fileData.filename}:`, processedData)
            console.log(`🔍 Is array?`, Array.isArray(processedData))
            console.log(`🔍 Length:`, processedData?.length)

            if (processedData && Array.isArray(processedData) && processedData.length > 0) {
              const fileResult = {
                filename: fileData.filename,
                subid: fileData.subid,
                datasetId: fileData.datasetId,
                status: 'success',
                sessionId: responseData?.session_id,
                originalRows: responseData?.original_rows,
                processedRows: responseData?.processed_rows,
                dayCount: responseData?.day_count,
                headers: responseData?.headers
              }
              this.fileResults.push(fileResult)
              this.successfulFiles.push(fileResult)
            } else if (parsedResponseData && parsedResponseData.success === false) {
              console.log(`❌ Backend reported failure for ${fileData.filename}:`, parsedResponseData.message)
              const fileResult = {
                filename: fileData.filename,
                subid: fileData.subid,
                datasetId: fileData.datasetId,
                status: 'failed',
                error: parsedResponseData.message || 'Processing failed'
              }
              this.fileResults.push(fileResult)
              this.failedFiles.push(fileResult)
            } else {
              console.log(`❌ No valid data found in response for ${fileData.filename}`)
              console.log(`❌ processedData:`, processedData)
              const fileResult = {
                filename: fileData.filename,
                subid: fileData.subid,
                datasetId: fileData.datasetId,
                status: 'failed',
                error: 'Upload failed - no data received'
              }
              this.fileResults.push(fileResult)
              this.failedFiles.push(fileResult)
            }
              } catch (fileError) {
                console.error(`File upload error for ${fileData.filename}:`, fileError)
                console.error('Error response data:', fileError.response?.data)
                console.error('Error response status:', fileError.response?.status)
                const fileResult = {
                  filename: fileData.filename,
                  subid: fileData.subid,
                  datasetId: fileData.datasetId,
                  status: 'failed',
                  error: fileError.response?.data?.message || `Upload failed with ${fileError.response?.status} error`
                }
                this.fileResults.push(fileResult)
                this.failedFiles.push(fileResult)
              }
        }

        // Create summary result
        this.uploadResult = {
          totalFiles: this.cohortFiles.length,
          successfulFiles: this.successfulFiles.length,
          failedFiles: this.failedFiles.length,
          fileResults: this.fileResults
        }

        // Update global state with the first successful file's session (if any)
        if (this.successfulFiles.length > 0) {
          const firstSuccess = this.successfulFiles[0]
          this.updateGlobalState({
            configuredData: firstSuccess,
            subid: 'COHORT',
            datasetId: 'MULTI',
            sessionId: firstSuccess.sessionId
          })
        }

        // Auto-navigate to cohort dashboard if all files succeeded
        if (this.failedFiles.length === 0) {
          // Store cohort files in global state for CohortDashboard
          this.updateGlobalState({
            cohortFiles: this.cohortFiles
          })
          
          setTimeout(() => {
            this.$router.push(this.toLab('/cohort-dashboard'))
          }, 2000)
        }

      } catch (error) {
        console.error('Cohort upload error:', error)
        this.error = 'Cohort processing failed. Please try again.'
      } finally {
        this.cohortUploading = false
        this.currentProcessingFile = null
      }
    },

    retryFailedFiles() {
      // Reset failed files back to cohort files for retry
      this.cohortFiles = this.failedFiles.map(failedFile => {
        // Find the original file object from the initial upload
        const originalFile = this.cohortFiles.find(f => f.filename === failedFile.filename) || 
                           this.invalidFilenames.find(f => f.filename === failedFile.filename)
        return {
          filename: failedFile.filename,
          file: originalFile?.file,
          subid: failedFile.subid,
          datasetId: failedFile.datasetId
        }
      }).filter(fileData => fileData.file) // Only include files that still have the file object

      // Clear results and start upload again
      this.uploadResult = null
      this.fileResults = []
      this.successfulFiles = []
      this.failedFiles = []
      this.error = null

      // Start upload process
      this.uploadCohortFiles()
    },

    clearPreviousCohortData() {
      // Clear cohort-specific data from global state
      this.updateGlobalState({
        cohortFiles: [],
        cohortProcessingResults: null
      })
      
      // Clear cohort-specific data from session storage
      sessionStorage.removeItem('cohortFiles')
      sessionStorage.removeItem('cohortProcessingResults')
      
      console.log('🧹 Cleared previous cohort data from global state and session storage')
    }
  },
  mounted() {
    // Clear any previous cohort processing data when entering cohort upload
    this.clearPreviousCohortData()
    
    // Set up back button to return to upload choice
    this.setBackButton({
      text: 'Back to Upload Options',
      route: '/'
    })
  }
}
</script>
